'use client';

import { ChangeEvent } from 'react';
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form';

type UseFieldProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> & {
  onChange?: (
    event: PathValue<TFieldValues, Path<TFieldValues>>
  ) => void | ((event: ChangeEvent) => void);
};

export const useField = <TFieldValues extends FieldValues>({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  onChange: _onChange,
}: UseFieldProps<TFieldValues>) => {
  const context = useFormContext<TFieldValues>();
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<TFieldValues>({
    name,
    control: control ?? context?.control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const errorMessage = fieldState.error?.message;

  const onChange = (event: unknown) => {
    fieldOnChange(event);
    _onChange?.(event);
  };

  return {
    ...field,
    value,
    checked: value,
    errorMessage,
    error: errorMessage,
    onChange,
  };
};
